import { Routes, Route, Navigate } from 'react-router-dom';
import { Role } from '@hirenfire/shared';
import { useAuthStore } from '@/stores/authStore';
import { AuthGuard } from '@/guards/AuthGuard';
import { RoleGuard } from '@/guards/RoleGuard';
import { Shell } from '@/components/layout/Shell';

// Auth pages
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { PhoneVerifyPage } from '@/pages/auth/PhoneVerifyPage';

// Dashboard pages
import { WorkerDashboard } from '@/pages/dashboard/WorkerDashboard';
import { AgencyDashboard } from '@/pages/dashboard/AgencyDashboard';
import { AdminDashboard } from '@/pages/dashboard/AdminDashboard';

// Job pages
import { JobsListPage } from '@/pages/jobs/JobsListPage';
import { JobDetailPage } from '@/pages/jobs/JobDetailPage';
import { CreateJobPage } from '@/pages/jobs/CreateJobPage';

// Application pages
import { MyApplicationsPage } from '@/pages/applications/MyApplicationsPage';

// Contract pages
import { ContractsPage } from '@/pages/contracts/ContractsPage';
import { ContractDetailPage } from '@/pages/contracts/ContractDetailPage';

// Chat pages
import { ChatPage } from '@/pages/chat/ChatPage';

// Payment pages
import { PaymentsPage } from '@/pages/payments/PaymentsPage';
import { WalletPage } from '@/pages/payments/WalletPage';

// Profile pages
import { ProfilePage } from '@/pages/profile/ProfilePage';
import { EditProfilePage } from '@/pages/profile/EditProfilePage';
import { PublicProfilePage } from '@/pages/profile/PublicProfilePage';

// KYC pages
import { KYCPage } from '@/pages/kyc/KYCPage';

// Settings pages
import { SettingsPage } from '@/pages/settings/SettingsPage';

// Admin pages
import { AdminUsersPage } from '@/pages/admin/AdminUsersPage';
import { AdminKYCPage } from '@/pages/admin/AdminKYCPage';
import { AdminEscrowPage } from '@/pages/admin/AdminEscrowPage';
import { AdminAuditPage } from '@/pages/admin/AdminAuditPage';
import { AdminBansPage } from '@/pages/admin/AdminBansPage';

// Error pages
import { NotFoundPage } from '@/pages/errors/NotFoundPage';
import { ForbiddenPage } from '@/pages/errors/ForbiddenPage';

function DashboardRedirect() {
  const user = useAuthStore((s) => s.user);

  if (!user) return <Navigate to="/login" replace />;

  switch (user.role) {
    case Role.ADMIN:
      return <AdminDashboard />;
    case Role.AGENCY:
      return <AgencyDashboard />;
    case Role.WORKER:
    default:
      return <WorkerDashboard />;
  }
}

function AuthenticatedRoutes() {
  return (
    <AuthGuard>
      <Shell>
        <Routes>
          {/* Dashboard */}
          <Route path="/dashboard" element={<DashboardRedirect />} />

          {/* Jobs */}
          <Route path="/jobs" element={<JobsListPage />} />
          <Route path="/jobs/create" element={
            <RoleGuard roles={[Role.AGENCY]}>
              <CreateJobPage />
            </RoleGuard>
          } />
          <Route path="/jobs/:id" element={<JobDetailPage />} />

          {/* Applications */}
          <Route path="/applications" element={
            <RoleGuard roles={[Role.WORKER]}>
              <MyApplicationsPage />
            </RoleGuard>
          } />

          {/* Contracts */}
          <Route path="/contracts" element={<ContractsPage />} />
          <Route path="/contracts/:id" element={<ContractDetailPage />} />

          {/* Chat */}
          <Route path="/chat" element={<ChatPage />} />

          {/* Payments */}
          <Route path="/payments" element={<PaymentsPage />} />
          <Route path="/wallet" element={<WalletPage />} />

          {/* Profile */}
          <Route path="/profile/me" element={<ProfilePage />} />
          <Route path="/profile/me/edit" element={<EditProfilePage />} />
          <Route path="/profile/:userId" element={<PublicProfilePage />} />

          {/* KYC */}
          <Route path="/kyc" element={<KYCPage />} />

          {/* Settings */}
          <Route path="/settings" element={<SettingsPage />} />

          {/* Admin */}
          <Route path="/admin/users" element={
            <RoleGuard roles={[Role.ADMIN]}>
              <AdminUsersPage />
            </RoleGuard>
          } />
          <Route path="/admin/kyc" element={
            <RoleGuard roles={[Role.ADMIN]}>
              <AdminKYCPage />
            </RoleGuard>
          } />
          <Route path="/admin/escrow" element={
            <RoleGuard roles={[Role.ADMIN]}>
              <AdminEscrowPage />
            </RoleGuard>
          } />
          <Route path="/admin/audit" element={
            <RoleGuard roles={[Role.ADMIN]}>
              <AdminAuditPage />
            </RoleGuard>
          } />
          <Route path="/admin/bans" element={
            <RoleGuard roles={[Role.ADMIN]}>
              <AdminBansPage />
            </RoleGuard>
          } />

          {/* Catch-all inside shell */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Shell>
    </AuthGuard>
  );
}

export function AppRouter() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/register/verify-phone" element={<PhoneVerifyPage />} />

      {/* Error routes */}
      <Route path="/403" element={<ForbiddenPage />} />

      {/* Root redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* All authenticated routes */}
      <Route path="/*" element={<AuthenticatedRoutes />} />
    </Routes>
  );
}
